import { type FC, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import { getSettlementSearch } from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";

import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { useAsyncOptions } from "@/hooks/useAsyncOptions";
import type { NewAddressFormValues } from "@/types/form";

import type { FieldName } from "./AddAddressModal.constants";

interface SettlementFieldProps {
    regionId: string;
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const SettlementField: FC<SettlementFieldProps> = ({
    regionId,
    activeMap,
    resetBelow,
}) => {
    const [searchQuery, setSearchQuery] = useState("");

    const { setValue } = useFormContext<NewAddressFormValues>();

    const { options: fetchedOptions, loading } = useAsyncOptions({
        fetchFn: (signal) =>
            getSettlementSearch(
                {
                    query: searchQuery,
                    area: regionId,
                },
                {
                    axiosOverrides: { signal },
                },
            ),
        selectData: (response) => response.result,
        mapToOption: (s) => ({
            label: `${s.name} (${s?.region?.name ?? "Невідомий район"}, ${
                s?.community?.name ?? "Невідома громада"
            })`,
            value: s.id,
        }),
        dependencies: [searchQuery],
        enabled:
            (!searchQuery || searchQuery.length >= 3) && activeMap.settlement,
        debounceMs: 500,
    });

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQuery(value);
            setValue("settlement", "");
            resetBelow("settlement");
        },
        [resetBelow, setValue],
    );

    return (
        <RHFAutocomplete
            name="settlement"
            options={fetchedOptions}
            label="Місто"
            rules={{
                required: validationMessage.required,
            }}
            allowClear={false}
            disabled={!activeMap.settlement}
            onAfterChange={() => resetBelow("settlement")}
            loading={loading}
            onInputChange={handleInputChange}
            selectProps={{
                size: "large",
            }}
        />
    );
};
